import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, LockKeyhole, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { Brand } from "../components/Brand";
import { useSession } from "../lib/session";
import { api } from "../lib/api";

type Step = "phone" | "otp" | "pin" | "register";

export function Login() {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("712 345 678");
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [pin, setPin] = useState("");
  const [deviceChallenge, setDeviceChallenge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [region, setRegion] = useState("Dar es Salaam");
  const [challengeId, setChallengeId] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const otpBoxes = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmitted = useRef("");
  const { signIn, signOut, authenticated } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (authenticated) navigate("/", { replace: true });
  }, [authenticated, navigate]);

  useEffect(() => {
    if (step !== "otp" || resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [step, resendIn]);

  const msisdn = useMemo(() => {
    const d = phone.replace(/\D/g, "");
    if (d.startsWith("0")) return "255" + d.slice(1);
    if (d.startsWith("255")) return d;
    return "255" + d;
  }, [phone]);

  const handleRequestCode = async () => {
    if (!/^255\d{8,9}$/.test(msisdn)) {
      setError("Enter a valid Tanzanian mobile number (e.g. 712 345 678).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const challenge: any = await api.requestOtp(msisdn);
      setChallengeId(challenge.challenge_id ?? "");
      setBusy(false);
      setCode(["", "", "", "", "", ""]);
      setResendIn(45);
      setStep("otp");
    } catch (e: any) {
      setError(e?.problem?.detail || "Failed to request OTP");
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || busy) return;
    await handleRequestCode();
  };

  const handleVerifyCode = async () => {
    if (code.join("").length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result: any = await api.verifyOtp(challengeId, code.join(""));
      setDeviceChallenge(result.device_challenge ?? "");
      setBusy(false);
      if (result.registered) {
        setStep("pin");
      } else {
        setStep("register");
      }
    } catch (e: any) {
      setError(e?.problem?.detail || "Invalid or expired OTP");
      setBusy(false);
      applyCode(["", "", "", "", "", ""]);
      focusBox(0);
    }
  };

  const handleSubmitPin = async () => {
    if (!/^\d{4,6}$/.test(pin)) {
      setError("Enter your 4–6 digit PIN.");
      return;
    }
    if (!deviceChallenge) {
      setError("This sign-in attempt expired. Request a new code.");
      setStep("phone");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await api.loginWithPin(deviceChallenge, pin, msisdn);
      await signIn(session);
      navigate("/", { replace: true });
    } catch (e: any) {
      setError(e?.problem?.detail || "Incorrect PIN");
      setBusy(false);
    }
  };

  const handleSubmitRegister = async () => {
    if (!msisdn) return;
    if (!businessName.trim() || !region.trim()) {
      setError("Business name and region are required.");
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      setError("Choose a 4–6 digit PIN.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await api.register({
        msisdn,
        business_name: businessName.trim(),
        region: region.trim(),
        pin,
      });
      await signIn(session);
      navigate("/", { replace: true });
    } catch (e: any) {
      setError(e?.problem?.detail || "Registration failed");
      setBusy(false);
    }
  };

  const goTo = (next: Step) => {
    setError(null);
    setPin("");
    setStep(next);
  };

  const resetToPhone = () => {
    setChallengeId("");
    setDeviceChallenge("");
    const newCode = ["", "", "", "", "", ""];
    setCode(newCode);
    setStep("phone");
  };

  const applyCode = (next: string[]) => {
    autoSubmitted.current = "";
    setCode(next);
  };

  const focusBox = (index: number) => {
    const box = otpBoxes.current[index];
    if (!box) return;
    box.focus();
    box.select();
  };

  const writeDigits = (index: number, digits: string) => {
    const next = [...code];
    let cursor = index;
    for (const digit of digits) {
      if (cursor > 5) break;
      next[cursor] = digit;
      cursor += 1;
    }
    applyCode(next);
    return Math.min(cursor, 5);
  };

  const onPaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6 - index);
    if (!digits) return;
    focusBox(writeDigits(index, digits));
  };

  const onOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (code[index]) {
        applyNext(index, "");
      } else if (index > 0) {
        applyNext(index - 1, "");
        focusBox(index - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
      return;
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      focusBox(index + 1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      void handleVerifyCode();
    }
  };

  function applyNext(index: number, digit: string) {
    const next = [...code];
    next[index] = digit;
    applyCode(next);
  }

  function setDigit(index: number, value: string) {
    const digits = value.replace(/\D/g, "");
    if (digits.length > 1) {
      focusBox(writeDigits(index, digits));
      return;
    }
    const next = [...code];
    next[index] = digits;
    applyCode(next);
    if (digits && index < 5) focusBox(index + 1);
  }

  useEffect(() => {
    if (step !== "otp") return;
    focusBox(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const joined = code.join("");
    if (step !== "otp" || busy || joined.length !== 6 || autoSubmitted.current === joined) return;
    autoSubmitted.current = joined;
    void handleVerifyCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step, busy]);

  return (
    <div className="auth">
      <section className="auth-art" aria-hidden>
        <div className="auth-orb auth-orb-a" />
        <div className="auth-orb auth-orb-b" />
        <div className="auth-art-top">
          <Brand />
          <span className="auth-pill">
            <Sparkles size={13} /> Enabling Opportunities
          </span>
        </div>

        <div className="auth-art-copy">
          <h1>
            One wallet.
            <br />
            Every network.
            <br />
            <span>Every sale.</span>
          </h1>
          <p>
            Fund once, sell airtime to any Tanzanian network, and see every shilling settle in real time — on the web,
            on mobile, or on USSD.
          </p>
          <ul className="auth-points">
            <li><BadgeCheck size={17} /> One TZS balance across Vodacom, Airtel, Tigo and Halotel</li>
            <li><ShieldCheck size={17} /> Reserve-first selling — never send twice when a network is slow</li>
            <li><Smartphone size={17} /> The same account on web, app and USSD</li>
          </ul>
        </div>

        <div className="auth-art-foot">
          <span>Operated by SKT Tanzania Limited</span>
          <span>www.skttanzania.co.tz</span>
        </div>
      </section>

      <section className="auth-form-wrap">
        <div className="auth-form">
          <div className="auth-form-top">
            <span className="ev-badge ev-badge-neutral">Agent portal</span>
            <span className="auth-lang">EN <span>/</span> SW</span>
          </div>

          {step === "phone" ? (
            <>
              <header className="auth-head">
                <h2>Welcome back</h2>
                <p>Sign in with the mobile number registered to your evocha agent account.</p>
              </header>

              <form onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleRequestCode(); }} className="auth-steps">
                <label className="ev-field">
                  <span className="ev-label">Mobile number</span>
                  <div className="ev-input-affix">
                    <span className="ev-affix ev-affix-left">+255</span>
                    <input
                      className="ev-input"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                      placeholder="712 345 678"
                      aria-describedby="phone-hint"
                    />
                  </div>
                  <span className="ev-hint" id="phone-hint">We will send a 6-digit code by SMS.</span>
                </label>

                {error && <p className="ev-error" role="alert">{error}</p>}

                <button
                  className="ev-btn ev-btn-gradient ev-btn-lg ev-btn-block"
                  disabled={busy}
                  type="submit"
                >
                  {busy ? "Sending code…" : "Send code"}
                </button>

                <p className="auth-terms">
                  By continuing you agree to the evocha <a href="#terms">agent terms</a> and{" "}
                  <a href="#privacy">privacy notice</a>.
                </p>
              </form>
            </>
          ) : (
            <>
              {step === "otp" && (
                <>
                  <header className="auth-head">
                    <h2>Enter your code</h2>
                    <p>
                      We sent a 6-digit code to <strong>+{msisdn}</strong> by SMS.
                    </p>
                  </header>
                  <div className="auth-steps">
                    <div className="otp-row">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          otpBoxes.current[index] = el;
                        }}
                        className="otp-box"
                        inputMode="numeric"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        maxLength={1}
                        value={code[index] ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDigit(index, e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => onOtpKeyDown(e, index)}
                        onPaste={(e: React.ClipboardEvent) => onPaste(e, index)}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                        disabled={busy}
                        aria-label={`Digit ${index + 1}`}
                      />
                    ))}
                  </div>
                  {error && <p className="ev-error" role="alert">{error}</p>}
                  <button
                    className="ev-btn ev-btn-gradient ev-btn-lg ev-btn-block"
                    disabled={code.join("").length !== 6 || busy}
                    onClick={handleVerifyCode}
                    type="button"
                  >
                    {busy ? "Verifying…" : "Verify and continue"}
                  </button>
                  <div className="auth-resend">
                    <span>Didn't get it?</span>
                    <button
                      type="button"
                      className="link-button"
                      disabled={resendIn > 0 || busy}
                      onClick={handleResend}
                    >
                      {resendIn > 0 ? `Resend in 0:${String(resendIn).padStart(2, "0")}` : "Resend code"}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="link-button auth-alt-action"
                    onClick={resetToPhone}
                  >
                    Use a different number
                  </button>
                  </div>
                </>
              )}

              {step === "pin" && (
                <>
                  <header className="auth-head">
                    <h2>Enter your PIN</h2>
                    <p>Confirm the 4–6 digit PIN for your agent account to continue.</p>
                  </header>
                  <div className="auth-steps">
                  <input
                    className="otp-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={pin}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="••••"
                    autoFocus
                  />
                  {error && <p className="ev-error" role="alert">{error}</p>}
                  <button
                    className="ev-btn ev-btn-gradient ev-btn-lg ev-btn-block"
                    disabled={busy}
                    onClick={handleSubmitPin}
                    type="button"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={resetToPhone}
                    disabled={busy}
                    type="button"
                    className="link-button auth-alt-action"
                  >
                    Use a different number
                  </button>
                  </div>
                </>
              )}

              {step === "register" && (
                <>
                  <header className="auth-head">
                    <h2>Create your account</h2>
                    <p>
                      No account exists for <strong>+{msisdn}</strong>. Set up your agent
                      wallet in a few seconds.
                    </p>
                  </header>
                  <div className="auth-steps">
                  <div className="auth-fields">
                    <label className="ev-field">
                      <span className="ev-label">Business name</span>
                      <input
                        className="ev-input"
                        value={businessName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusinessName(e.target.value)}
                        placeholder="Amani Mobile"
                        autoCapitalize="words"
                      />
                    </label>
                    <label className="ev-field">
                      <span className="ev-label">Region</span>
                      <input
                        className="ev-input"
                        value={region}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRegion(e.target.value)}
                        placeholder="Dar es Salaam"
                        autoCapitalize="words"
                      />
                    </label>
                    <label className="ev-field">
                      <span className="ev-label">Choose a PIN</span>
                      <input
                        className="ev-input"
                        value={pin}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        placeholder="4–6 digits"
                        type="password"
                        autoCapitalize="none"
                      />
                    </label>
                  </div>
                  {error && <p className="ev-error" role="alert">{error}</p>}
                  <button
                    className="ev-btn ev-btn-gradient ev-btn-lg ev-btn-block"
                    disabled={busy}
                    onClick={handleSubmitRegister}
                    type="button"
                  >
                    Create account
                  </button>
                  <button
                    onClick={resetToPhone}
                    disabled={busy}
                    type="button"
                    className="link-button auth-alt-action"
                  >
                    Back to sign in
                  </button>
                  </div>
                </>
              )}
            </>
          )}

          <footer className="auth-foot">
            <span>© 2026 evocha</span>
            <a href="#help">Need help signing in?</a>
          </footer>
        </div>
      </section>
    </div>
  );
}